import * as localHumanMadeObjectHelpers from "@/helpers/HumanMadeObject";
import * as configHelper from "@/helpers/configHelper";

const END_OF_FIELD_VALUE = ", ";
const END_OF_LINE = "\n";
const MAX_ARTISTS_TO_DISPLAY = 3;
const DEDUPE_ARTISTS = configHelper.hideArtistDupes();
const DISPLAY_ARTIST_DESCRIPTION_INSTEAD_OF_NAME =
  configHelper.useArtistLongDescription();

/**
 * Checks whether a value is valid
 * @param {string|object|list} fieldValue - the field value to check
 *
 * @return {boolean} -- whether the field value is empty
 */
function isValid(fieldValue) {
  //check if human made object field value is valid
  if (
    fieldValue == undefined ||
    fieldValue == "" ||
    Object.keys(fieldValue).length == 0 ||
    fieldValue.length == 0
  ) {
    return false;
  }
  return true;
}

/**
 * Appends list value
 * @param {Array} list - the current line (list)
 * @param {String} _value - the current value to append
 *
 */
function appendValue(list, _value) {
  let value = _value;
  if (
    !isValid(_value) ||
    (typeof value == "string" && !isValid(_value.trim()))
  ) {
    return;
  }
  if (typeof value == "string") {
    value = value.trim();
    if (value.endsWith(",")) {
      value = value.substring(0, value.length - 1);
    }
  }
  list.push(value);
}

/**
 * Creates Museum Caption Text
 * @param {object} details - the details object for a ManMadeObject
 * @param {object} artists - a list of Artists
 *
 * @return {string} -- the citation string
 */
export function createCaptionLines(details, artists) {
  const artistInfo =
    localHumanMadeObjectHelpers.getArtistWithDetailsString(artists);
  let captionLines = []; //accumulator for all of the lines in captions text
  let secondLine = []; //accumulator for the second line in captions text
  let thirdLine = []; //accumulator for the third line in captions text
  //add human made object field values to the second caption line
  appendValue(secondLine, details["title"]);
  if (details["date"].length > 0) {
    appendValue(secondLine, details["date"]);
  }
  appendValue(secondLine, details["medium"]);
  //add human made object field values to the third caption line
  appendValue(thirdLine, details["dimensions"]);
  appendValue(thirdLine, details["object_number"]);
  //add lines to captions

  if (artistInfo) {
    captionLines.push(artistInfo); //add first caption line to captions
  }
  appendValue(captionLines, secondLine.join(END_OF_FIELD_VALUE));

  //add second caption line to captions
  appendValue(captionLines, thirdLine.join(END_OF_FIELD_VALUE));

  if (isValid(details["credit_line"])) {
    captionLines.push(details["credit_line"]); //add fourth caption line to captions
  }
  return captionLines.join(END_OF_LINE); //join to retrieve caption text
}

/**
 * Takes an array of artists and returns one less than or equal to the MAX_ARTISTS_TO_DISPLAY
 *
 * @param {array} artists - an Array of artists
 *
 * @return {array} artists - an Array of artists
 */
export function sliceArrayOfArtistsForCaption(artists) {
  if (shouldSliceArrayOfArtistsForCaption(artists)) {
    return artists.slice(0, MAX_ARTISTS_TO_DISPLAY);
  }
  return artists;
}

/**
 * Takes an array of artists and returns whether it is less than or equal to the MAX_ARTISTS_TO_DISPLAY
 *
 * @param {array} artists - an Array of artists
 *
 * @return {boolean} - whether the array is longer than the MAX_ARTISTS_TO_DISPLAY
 */
export function shouldSliceArrayOfArtistsForCaption(artists) {
  return artists.length > MAX_ARTISTS_TO_DISPLAY ? true : false;
}

/**
 * Takes an array of artists and returns one less than or equal to the MAX_ARTISTS_TO_DISPLAY
 *
 * @param {array} artists - an Array of artists
 * @param {function} translate - the translate function
 *
 * @return {array} artists - an Array of artists
 */
export function buildArtistListForSearchResults(artists, translate) {
  let artistList = [];

  if (artists) {
    artists =
      DEDUPE_ARTISTS && artists.length > 1
        ? localHumanMadeObjectHelpers.dedupeArtists(artists, "primary_name")
        : artists;
    let total = artists.length;

    artists.slice(0, MAX_ARTISTS_TO_DISPLAY).forEach((artist) => {
      let displayName;
      if (DISPLAY_ARTIST_DESCRIPTION_INSTEAD_OF_NAME) {
        displayName = artist.description;
      } else {
        displayName = artist.primary_name;
      }

      if (displayName.toLowerCase() == "unknown") {
        displayName = translate("artist.unknown");
      }
      artistList.push(displayName);
    });
    if (total > MAX_ARTISTS_TO_DISPLAY) {
      artistList.push(additionalArtistsSubtitle(total, translate));
    }
  }

  return artistList;
}

/**
 * Takes the total number of artists and a translate function to return a string detailing the
 * number of additional artists beyond the MAX_ARTISTS_TO_DISPLAY
 *
 * @param {integer} total - an Array of artists
 * @param {function} translate - the translate function
 *
 * @return {string} translation string - ie. "and 2 more..."
 */
export function additionalArtistsSubtitle(total, translate) {
  if (total <= MAX_ARTISTS_TO_DISPLAY) {
    return undefined;
  }

  let remainingTotal = total - MAX_ARTISTS_TO_DISPLAY;
  return translate("artist_and_title.moreArtistsSubtitle", {
    totalArtists: remainingTotal
  });
}
